import { isOfficeUserRole } from '@till-erp/platform/user-values';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { navFor, TASKS_PATH, type NavArea, type NavItem, type RoleNav } from '../../navigation.ts';
import { apiFetch } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { cn } from '../../lib/utils.ts';

/**
 * Gemeinsame Teile der Navigation (Change 11.10.2026 Design S3, Story 1.77): Logo, Zähler, aktiver Bereich. Genutzt von
 * Symbolleiste, breiter Leiste und Menü-Schublade am Handy.
 */

/**
 * Zähler „Aufgaben“ (EXPERIENCE §2.1): fällige und überfällige Wiedervorlagen plus Klärfälle aus „Meine“ (mir zugewiesen
 * oder niemandem zugewiesen in meinem Bereich).
 */
export function useTaskCount(): number {
  const { getToken, me } = useSession();
  const count = useQuery({
    queryKey: ['tasks', 'count'],
    queryFn: () => apiFetch<{ count: number }>(getToken, '/api/v1/tasks/count'),
    // Ticket 1.95: Packplatz hat keine Aufgaben (Route gesperrt, kein Eintrag in der Leiste). Story 23.17: Mitarbeiter
    // zählt wie im Büro (der Server zählt für die Rolle nur Aufgaben, keine Klärfälle).
    enabled: isOfficeUserRole(me.role) || me.role === 'mitarbeiter',
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  return count.data?.count ?? 0;
}

/** Navigation der angemeldeten Rolle (Ticket 1.95: Packplatz nur „Versand“). */
export function useRoleNav(): RoleNav {
  const { me } = useSession();
  return navFor(me.role);
}

/** Zähler eines Eintrags (nur „Aufgaben“ hat einen). */
export function useCounterOf(): (item: NavItem) => number | undefined {
  const taskCount = useTaskCount();
  return (item) => (item.path === TASKS_PATH ? taskCount : undefined);
}

/** Name eines Eintrags für Hilfsmittel; bei Zähler „Aufgaben, 6 offen“. */
export function navName(item: NavItem, counter?: number): string {
  return counter ? `${item.label}, ${counter} offen` : item.label;
}

/** Liegt `path` im Eintrag? Start nur genau, sonst auch Unterseiten (wie der aktive Link des Routers). */
export function pathMatches(pathname: string, path: string): boolean {
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** Aktueller Pfad (für den aktiven Bereich). */
export function usePathname(): string {
  return useRouterState({ select: (s) => s.location.pathname });
}

export function areaActive(area: NavArea, pathname: string): boolean {
  return area.items.some((i) => pathMatches(pathname, i.path));
}

/**
 * Zähler-Abzeichen. Text für Hilfsmittel „: 6 offen“ (sichtbar nur die Zahl); `corner` = kleines Abzeichen an der
 * Ecke des runden Symbols (Symbolleiste), sonst rechts in der Zeile.
 */
export function CounterBadge({ count, corner }: { count: number; corner?: boolean }) {
  return (
    <span
      data-testid="nav-counter"
      aria-hidden={corner ? true : undefined}
      className={cn(
        'grid place-items-center rounded-full bg-brand leading-none text-on-brand tabular-nums',
        corner
          ? 'absolute -top-[3px] -right-[5px] h-[18px] min-w-[18px] px-[5px] text-2xs font-medium ring-2 ring-[var(--surface-side)]'
          : 'ml-auto h-[22px] min-w-[22px] px-[7px] text-caption',
      )}
    >
      <span className="sr-only">: </span>
      {count}
      <span className="sr-only"> offen</span>
    </span>
  );
}

/** Logo-Marke „TT“: kleines Quadrat in TILL-Dunkelblau (Redesign 31.10.2026, Variante C). */
export function LogoMark() {
  return (
    <span
      role="img"
      aria-label="TILL TOOLS ERP"
      className="grid size-7 flex-none place-items-center rounded-[var(--radius-logo)] bg-primary text-caption font-semibold tracking-[-0.02em] text-on-brand"
    >
      <span aria-hidden>TT</span>
    </span>
  );
}

/** Wortmarke neben dem Logo (breite Leiste, Schublade): „TILL TOOLS ERP“. */
export function Wordmark({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn('min-w-0 truncate text-row leading-tight font-semibold tracking-[-0.01em] whitespace-nowrap text-ink', className)}>
      TILL TOOLS <span className="font-normal text-ink-3">ERP</span>
    </span>
  );
}

/** Profil-Kreis mit Kürzel (Symbolleiste unten, breite Leiste, Schublade). */
export function Avatar({ initials }: { initials: string }) {
  return (
    <span className="grid size-8 flex-none place-items-center rounded-full bg-surface-card-2 text-caption font-semibold text-ink">{initials}</span>
  );
}
